import React, { forwardRef, useState, useEffect } from "react";
import { TextField, Text } from "@radix-ui/themes";

interface IntegerInputProps
  extends Omit<TextField.RootProps, "type" | "onChange" | "value"> {
  label?: string;
  value?: number;
  onChange?: (value: number | undefined) => void;
  min?: number;
  max?: number;
  allowNegative?: boolean;
  placeholder?: string;
  error?: string;
}

export const IntegerInput = forwardRef<HTMLInputElement, IntegerInputProps>(
  (
    {
      label,
      value,
      onChange,
      min,
      max,
      allowNegative = true,
      placeholder,
      error,
      size = "2",
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>(
      value !== undefined ? String(value) : ""
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (newValue === "") {
        setInputValue("");
        onChange?.(undefined);
        return;
      }

      if (allowNegative && newValue === "-") {
        setInputValue("-");
        return;
      }

      const regex = allowNegative ? /^-?\d*$/ : /^\d*$/;
      if (!regex.test(newValue)) {
        return;
      }

      setInputValue(newValue);

      const numericValue = parseInt(newValue, 10);
      if (!isNaN(numericValue)) {
        onChange?.(numericValue);
      }
    };

    const handleBlur = () => {
      if (inputValue && inputValue !== "-") {
        const numericValue = parseInt(inputValue, 10);
        if (!isNaN(numericValue)) {
          let constrainedValue = numericValue;

          if (min !== undefined) {
            constrainedValue = Math.max(min, constrainedValue);
          }
          if (max !== undefined) {
            constrainedValue = Math.min(max, constrainedValue);
          }

          setInputValue(String(constrainedValue));
          onChange?.(constrainedValue);
        }
      } else if (inputValue === "-") {
        setInputValue("");
        onChange?.(undefined);
      }
    };

    useEffect(() => {
      if (value !== undefined && String(value) !== inputValue) {
        setInputValue(String(value));
      }
    }, [value, inputValue]);

    return (
      <Text as="label">
        {label && (
          <Text as="p" size="1" className="mb-0.5">
            {label}
          </Text>
        )}
        <TextField.Root
          ref={ref}
          size={size}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={{
            ...(props.style || {}),
            ...(props.disabled && { cursor: "not-allowed" }),
          }}
          {...props}
        />
        {error && (
          <Text size="1" color="red" as="p" className="mt-0.5">
            {error}
          </Text>
        )}
      </Text>
    );
  }
);
